import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { WeighResult, WeighTask } from '@popcart/core/v1';
import { api, errorMessage } from '../../api';
import { ExpressChip, Loading, arrowNav, useTitle } from '../../ui/bits';
import { Fig, Stat } from '../../ui/viz';
import { ProductArt, Scene, SceneError } from '../../catalog/art';
import { Icon } from '../../ui/Icon';
import { useToast } from '../../ui/toast';
import { clock, count, peso } from '../../lib/format.js';
import { QUEUE_MS, shopKeys, useShop } from '../ShopLayout';
import { applyResults, gramsLabel, groupTasks, isUnweighedConflict, nextTaskId, orderAction, toWeighCount, type WeighOrder } from './lib/weigh.js';
import { WeighPanel } from './WeighPanel';
import { ArrivalsTab } from './Arrivals';
import { freshKeys } from './keys';
import './fresh.css';


type Tab = 'weigh' | 'arrivals';

/** /shop/:shopId/fresh — the Fresh packing station: weigh queue + today's arrivals. */
export function FreshPage() {
  const { shopId, queue } = useShop();
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get('tab') === 'arrivals' ? 'arrivals' : 'weigh';
  useTitle(tab === 'arrivals' ? "Fresh · today's arrivals" : 'Fresh · weigh & pack');
  const setTab = (t: Tab) => {
    const p = new URLSearchParams(params);
    if (t === 'weigh') p.delete('tab');
    else p.set('tab', t);
    setParams(p, { replace: true });
  };
  const weigh = useQuery({
    queryKey: freshKeys.weigh(shopId),
    queryFn: () => api.weighQueue(shopId),
    refetchInterval: QUEUE_MS,
    refetchIntervalInBackground: true,
    enabled: !!shopId,
  });
  const left = useMemo(() => (weigh.data ?? []).filter((t) => !t.weighed).length, [weigh.data]);

  const pending = useMemo(() => new Set((weigh.data ?? []).filter((t) => !t.weighed).map((t) => t.shopOrderId)), [weigh.data]);
  const orders = pending.size;
  const lateOrders = queue.filter((q) => q.late && pending.has(q.shopOrderId)).length;

  return (
    <>
      <header className="dv-head">
        <div className="stack s4">
          <h1>Fresh · weigh &amp; pack</h1>
          <span className="dv-live" title="Express first, then oldest. The final price uses the actual weight.">
            Final price = actual weight
          </span>
        </div>
        <div className="dv-actions">
          <div className="seg ph-seg" role="tablist" aria-label="Fresh station" onKeyDown={(e) => arrowNav(e, '[role="tab"]')}>
            <button type="button" role="tab" id="fresh-tab-weigh" aria-selected={tab === 'weigh'} aria-controls="fresh-panel" tabIndex={tab === 'weigh' ? 0 : -1} onClick={() => setTab('weigh')}>
              <Icon name="scale" size="sm" />
              To weigh {weigh.data ? <b className="n">{left}</b> : null}
            </button>
            <button type="button" role="tab" id="fresh-tab-arrivals" aria-selected={tab === 'arrivals'} aria-controls="fresh-panel" tabIndex={tab === 'arrivals' ? 0 : -1} onClick={() => setTab('arrivals')}>
              <Icon name="clock" size="sm" />
              Today's arrivals
            </button>
          </div>
        </div>
      </header>
      {tab === 'weigh' && weigh.data ? (
        <div className="dv-stats fw-stats dv-rise">
          <Stat tone="ink" label="Lines to weigh" value={count(left)} visual={<span className="dv-disc fw-disc" aria-hidden="true"><Icon name="scale" /></span>} />
          <Stat label="Orders" value={count(orders)} />
          <Stat label="Late" value={count(lateOrders)} tone={lateOrders ? 'alert' : undefined} />
        </div>
      ) : null}
      <div id="fresh-panel" role="tabpanel" aria-labelledby={`fresh-tab-${tab}`} className="stack s24">
        {tab === 'weigh' ? <WeighQueue shopId={shopId} query={weigh} /> : <ArrivalsTab shopId={shopId} />}
      </div>
    </>
  );
}

function WeighQueue({ shopId, query }: { shopId: string; query: UseQueryResult<WeighTask[]> }) {
  const { queue } = useShop();
  const qc = useQueryClient();
  // Results saved on this screen, shown before the next poll lands (server stays authoritative).
  const [results, setResults] = useState<Record<string, WeighResult>>({});
  const tasks = useMemo(() => applyResults(query.data ?? [], results), [query.data, results]);
  const groups = useMemo(() => groupTasks(tasks, queue), [tasks, queue]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [announce, setAnnounce] = useState('');

  const all = groups.flatMap((g) => g.tasks);
  const selected = all.find((t) => t.orderItemId === selectedId) ?? all.find((t) => t.orderItemId === nextTaskId(groups, null)) ?? all[0] ?? null;
  const group = selected ? (groups.find((g) => g.shopOrderId === selected.shopOrderId) ?? null) : null;

  const onSaved = (t: WeighTask, r: WeighResult, message: string) => {
    setResults((m) => ({ ...m, [t.orderItemId]: r }));
    setAnnounce(message);
    void qc.invalidateQueries({ queryKey: freshKeys.weigh(shopId) });
    void qc.invalidateQueries({ queryKey: shopKeys.queue(shopId) });
    // Last line of the order: stay so "Ready for rider" is right there. Otherwise, next task.
    if (!r.allWeighed) {
      const next = nextTaskId(groups, t.orderItemId);
      if (next) setSelectedId(next);
    }
  };

  if (query.isError) {
    return (
      <section className="card">
        <SceneError error={query.error} onRetry={() => void query.refetch()} />
      </section>
    );
  }
  if (query.isLoading) return <Loading label="Loading the weigh queue" rows={3} />;

  return (
    <>
      <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {announce}
      </p>
      {groups.length === 0 ? (
        <section className="card">
          <Scene scene="fresh" title="Scale’s free">
            Fresh orders that need weighing show up here, Express first.
          </Scene>
        </section>
      ) : (
        <div className="fw">
          <section className="card dv-card fw-list dv-rise" style={{ ['--i' as string]: 1 }} aria-labelledby="fw-queue-h">
            <div className="dv-card-h">
              <h2 id="fw-queue-h">Weigh queue</h2>
              <span className="dv-chip" title="Express first, then oldest">
                {toWeighCount(groups)} to weigh
              </span>
            </div>
            <ul className="fw-orders" aria-label="Orders to weigh">
              {groups.map((g) => (
                <li key={g.shopOrderId}>
                  <OrderTasks group={g} selectedId={selected?.orderItemId ?? null} onSelect={setSelectedId} />
                </li>
              ))}
            </ul>
          </section>

          {selected && group ? (
            <section className={`card dv-card fw-panel dv-rise${group.late ? ' late' : ''}`} style={{ ['--i' as string]: 2 }} aria-labelledby="fw-order-h">
              <div className="fw-panel-h">
                <div className="stack s4" style={{ minWidth: 0 }}>
                  <h2 id="fw-order-h">
                    <span className="ph-code">{group.orderCode}</span>
                    {group.customerFirstName ? ` · ${group.customerFirstName}` : ''}
                  </h2>
                  <span className="dv-sub">Placed {clock(group.placedAt)}</span>
                </div>
                <div className="row wrap">
                  {group.speed === 'express' ? <ExpressChip /> : null}
                  {group.late ? <span className="dv-chip bad">Running late</span> : null}
                  <span className="fw-progress" aria-label={`${group.weighedCount} of ${group.tasks.length} weighed`}>
                    <Fig text={`${group.weighedCount} / ${group.tasks.length}`} size="md" />
                    <span className="dv-label">weighed</span>
                  </span>
                </div>
              </div>
              <WeighPanel
                key={selected.orderItemId}
                shopId={shopId}
                task={selected}
                lastResult={results[selected.orderItemId] ?? null}
                locked={group.status === 'ready' || group.status === 'picked_up' || group.status === 'delivered' || group.status === 'cancelled'}
                primary={!group.allWeighed}
                onSaved={onSaved}
                onConflict={() => void qc.invalidateQueries({ queryKey: ['shop', shopId] })}
              />
              <OrderFooter shopId={shopId} group={group} onAnnounce={setAnnounce} />
            </section>
          ) : null}
        </div>
      )}
    </>
  );
}

function OrderTasks({ group, selectedId, onSelect }: { group: WeighOrder; selectedId: string | null; onSelect(id: string): void }) {
  return (
    <div className={`fw-order${group.late ? ' late' : ''}`}>
      <div className="fw-order-h">
        <span className="ph-code">{group.orderCode}</span>
        {group.customerFirstName ? <span className="nm">{group.customerFirstName}</span> : null}
        <span className="grow" />
        {group.speed === 'express' ? <ExpressChip /> : null}
        {group.late ? <span className="dv-chip bad">Late</span> : null}
        <span className="dv-sub">{clock(group.placedAt)}</span>
      </div>
      <ul className="stack s4" aria-label={`${group.orderCode} lines`}>
        {group.tasks.map((t) => (
          <li key={t.orderItemId}>
            <button type="button" className="fw-task" aria-current={t.orderItemId === selectedId ? 'true' : undefined} onClick={() => onSelect(t.orderItemId)}>
              <ProductArt className="fw-task-art" name={t.productName} line="fresh" />
              <span className="fw-task-nm">
                <b>{t.productName}</b>
                <small>{[`${t.qty} × ${t.sizeLabel}`, t.prepLabel].filter(Boolean).join(' · ')}</small>
              </span>
              {t.weighed ? (
                <span className="fw-task-g done">
                  <Fig text={gramsLabel(t.weighed.actualGrams)} size="sm" animate={false} />
                  <Icon name="check" size="sm" />
                </span>
              ) : (
                <span className="fw-task-g">
                  <Fig text={gramsLabel(t.estGramsTotal)} size="sm" animate={false} />
                  <small>est {peso(t.estLineCentavos)}</small>
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function OrderFooter({ shopId, group, onAnnounce }: { shopId: string; group: WeighOrder; onAnnounce(s: string): void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [problem, setProblem] = useState<string | null>(null);
  const action = orderAction(group);
  const others = group.lines.filter((l) => l !== 'fresh').map((l) => (l === 'grill' ? 'Grill' : 'Grocery')).join(' and ');
  const btn = useRef<HTMLButtonElement>(null);
  const wasReady = useRef(action.kind === 'ready');
  // When the last line is saved the Volt appears; move focus to it so Enter keeps the flow going.
  useEffect(() => {
    if (action.kind === 'ready' && !wasReady.current) btn.current?.focus();
    wasReady.current = action.kind === 'ready';
  }, [action.kind]);

  const ready = useMutation({
    mutationFn: async (steps: ('accepted' | 'preparing' | 'ready')[]) => {
      for (const to of steps) await api.advanceShopOrder(shopId, group.shopOrderId, to);
    },
    onSuccess: () => {
      setProblem(null);
      const msg = `${group.orderCode} is ready for the rider.`;
      toast(msg);
      onAnnounce(msg);
      void qc.invalidateQueries({ queryKey: ['shop', shopId] });
    },
    onError: (err) => {
      setProblem(isUnweighedConflict(err) ? 'Weigh every Fresh item first. Someone may have changed this order; the list has been refreshed.' : errorMessage(err, "Couldn't update the order. Try again."));
      void qc.invalidateQueries({ queryKey: ['shop', shopId] });
    },
  });

  return (
    <div className="stack fw-foot">
      {problem ? (
        <div className="status-msg st-error" role="alert">
          {problem}
        </div>
      ) : null}
      <div className="row between wrap">
        <span className="dv-sub" style={{ maxWidth: '44ch' }}>
          {action.kind === 'weigh'
            ? `${action.left} Fresh ${action.left === 1 ? 'item' : 'items'} left to weigh`
            : action.kind === 'done'
              ? 'Packed · waiting for the rider'
              : action.kind === 'unknown'
                ? 'All weighed · finish the rest on Today'
                : `All weighed.${others ? ` Check the ${others} items too.` : ''}`}
        </span>
        {action.kind === 'ready' ? (
          <button ref={btn} type="button" className="btn btn-primary btn-lg" disabled={ready.isPending} aria-busy={ready.isPending || undefined} onClick={() => ready.mutate(action.steps)}>
            {ready.isPending ? 'Saving…' : 'Ready for rider'}
          </button>
        ) : null}
      </div>
    </div>
  );
}
